import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * Handles the return from https://auth.emergentagent.com/.
 * The URL is `<origin>/auth/callback#session_id=<id>`.
 * We synchronously read the hash from useLocation (never window.location.hash),
 * exchange it once, then navigate to the shelf.
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { exchangeGoogleSession } = useAuth();
  const processedRef = useRef(false);
  const [state, setState] = useState("processing");

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;
    const hash = location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setState("error");
      toast.error("no session in the callback url");
      setTimeout(() => navigate("/", { replace: true }), 1200);
      return;
    }
    const session_id = decodeURIComponent(match[1]);
    (async () => {
      try {
        await exchangeGoogleSession(session_id);
        // clear the hash without re-triggering
        window.history.replaceState(null, "", window.location.pathname);
        toast.success("welcome to your shelf.");
        navigate("/shelf", { replace: true });
      } catch (e) {
        setState("error");
        toast.error(e?.response?.data?.detail || "google sign-in failed");
        setTimeout(() => navigate("/", { replace: true }), 1500);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-[60vh] grid place-items-center">
      <div className="text-center font-ui text-sm text-muted-foreground" data-testid="auth-callback-state">
        <Loader2 className="w-6 h-6 mx-auto mb-3 animate-spin text-primary" />
        {state === "processing" ? "opening your shelf…" : "returning to the library…"}
      </div>
    </div>
  );
}
